/** Product category endpoints (`/categories`). Public list; management mutations. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteData,
  getData,
  patchData,
  postData,
} from "../lib/apiClient";
import { apiClient } from "../lib/apiClient";
import { ApiEnvelope } from "../types/api";
import { queryKeys } from "./queryKeys";
import { Category } from "../types/models";

export interface CreateCategoryInput {
  name: string;
  description?: string;
  isActive?: boolean;
}

export type UpdateCategoryInput = Partial<CreateCategoryInput>;

export const categoriesApi = {
  /** `/categories` returns a bare array (not paginated). */
  list: () =>
    apiClient
      .get<ApiEnvelope<Category[]>>("/categories")
      .then((r) => r.data.data),
  getById: (id: string) => getData<Category>(`/categories/${id}`),
  create: (input: CreateCategoryInput) =>
    postData<Category>("/categories", input),
  update: (id: string, input: UpdateCategoryInput) =>
    patchData<Category>(`/categories/${id}`, input),
  remove: (id: string) => deleteData<null>(`/categories/${id}`),
};

// ── Hooks ────────────────────────────────────────────────────────────

export const useCategories = () =>
  useQuery({
    queryKey: queryKeys.categories.list,
    queryFn: () => categoriesApi.list(),
  });

export const useCreateCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCategoryInput) => categoriesApi.create(input),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.categories.all }),
  });
};

export const useUpdateCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCategoryInput }) =>
      categoriesApi.update(id, input),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.categories.all }),
  });
};

export const useDeleteCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => categoriesApi.remove(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.categories.all }),
  });
};
